/** The pure half of `rosterProjects.ts`: no `api`, so vitest can load it. */

export interface RosterProject {
  product_id: string
  title: string
  name_mn: string
  on_roster: boolean
  position: number
  is_active: boolean
}

export interface ProjectNames {
  mn: string
  en: string
}

export interface RosterProjectPatch {
  name_mn: string
  on_roster: boolean
  position: number
}

/**
 * The name a roster project key is SHOWN as: Mongolian readers see the Mongolian name, others the English, each falling
 * back to the other when blank. A key with no entry is shown as itself so a stale value stays visible.
 */
export function projectLabel(
  names: Record<string, ProjectNames> | undefined,
  key: string,
  language: string | undefined,
): string {
  const entry = names?.[key]
  if (!entry) return key
  const mongolian = (language ?? '').toLowerCase().startsWith('mn')
  const first = mongolian ? entry.mn : entry.en
  const second = mongolian ? entry.en : entry.mn
  return first.trim() || second.trim() || key
}

/**
 * Which `projectNames` a label reads: the roster document's when in reach, else the map the page's own response served.
 * Neither: `undefined`, and a key reads as itself.
 */
export function namesInReach(
  storeNames: Record<string, ProjectNames> | undefined,
  served: Record<string, ProjectNames> | undefined,
): Record<string, ProjectNames> | undefined {
  return storeNames ?? served
}

export function rosterFaceOf(
  faces: readonly RosterProject[] | undefined,
  productId: string,
): RosterProjectPatch {
  const face = faces?.find((f) => f.product_id === productId)
  return {
    name_mn: face?.name_mn ?? '',
    on_roster: face?.on_roster ?? false,
    position: face?.position ?? 0,
  }
}

/** Project-type products not yet on the roster and still in use, by English name; `GET /api/roster/projects` serves only Projects plus those already on the roster. */
export function addableProjects(faces: readonly RosterProject[] | undefined): RosterProject[] {
  return (faces ?? [])
    .filter((f) => !f.on_roster && f.is_active)
    .sort((a, b) => a.title.localeCompare(b.title))
}

export function nextRosterPosition(faces: readonly RosterProject[] | undefined): number {
  const placed = (faces ?? []).filter((f) => f.on_roster).map((f) => f.position)
  return placed.length ? Math.max(...placed) + 1 : 0
}
